const LOBBY_META_KEYS = [
  "accessCode",
  "amountOfPlayers",
  "botFill",
  "public",
  "ranked",
  "timedTurns",
  "turnTime",
  "mode",
  "wager",
  "roomOwner",
  "status",
  "createdAt",
  "eventMeta",
  "teamCount",
  "teamOrder",
];

const MATCH_SUMMARY_EXCLUDED_KEYS = new Set(["players", "moves"]);

export const isObject = (value) =>
  value !== null && typeof value === "object" && !Array.isArray(value);

export const buildLobbyMeta = (room = {}) => {
  if (!isObject(room)) return null;

  const lobbyMeta = {};
  LOBBY_META_KEYS.forEach((key) => {
    if (room[key] !== undefined) {
      lobbyMeta[key] = room[key];
    }
  });

  if (!Object.keys(lobbyMeta).length) return null;
  return lobbyMeta;
};

export const buildRoomFromNodes = ({
  roomId,
  lobbyMeta,
  players,
  rootFallback = null,
}) => {
  const fallback = isObject(rootFallback) ? rootFallback : {};
  const effectiveLobbyMeta = buildLobbyMeta(lobbyMeta) || buildLobbyMeta(fallback);

  if (!effectiveLobbyMeta && !Object.keys(fallback).length) {
    return null;
  }

  const effectivePlayers =
    (isObject(players) && players) ||
    (isObject(fallback.players) ? fallback.players : {});

  const playerIds =
    Array.isArray(fallback.playerIds) && fallback.playerIds.length
      ? fallback.playerIds
      : Object.keys(effectivePlayers || {});

  return {
    ...fallback,
    ...effectiveLobbyMeta,
    id: roomId,
    lobbyMeta: effectiveLobbyMeta || null,
    players: effectivePlayers,
    playerIds,
  };
};

export const buildMatchSummaryV2 = (match = {}) => {
  if (!isObject(match)) return null;

  const summary = { schemaVersion: 2 };
  Object.entries(match).forEach(([key, value]) => {
    if (MATCH_SUMMARY_EXCLUDED_KEYS.has(key)) return;
    summary[key] = value;
  });

  const playerOrder = Array.isArray(match.players)
    ? match.players.map((player) => player?.id).filter(Boolean)
    : Array.isArray(match.playerOrder)
      ? match.playerOrder.filter(Boolean)
      : [];

  if (playerOrder.length) {
    summary.playerOrder = playerOrder;
  }

  if (!summary.lastMove && Array.isArray(match.moves) && match.moves.length) {
    summary.lastMove = match.moves[match.moves.length - 1];
  }

  return summary;
};

export const buildMatchPlayersV2 = (players = []) => {
  const byId = {};
  if (!Array.isArray(players)) return byId;

  players.forEach((player, index) => {
    if (!player?.id) return;
    byId[player.id] = {
      ...player,
      sortIndex: index,
    };
  });

  return byId;
};

export const buildRecentMovesV2 = (moves = [], maxMoves = 16) => {
  const recent = Array.isArray(moves) ? moves.slice(-maxMoves) : [];
  const out = {};

  recent.forEach((move, index) => {
    const seq =
      Number.isFinite(Number(move?.seq)) ? Number(move.seq) : index + 1;
    const timestamp = Number.isFinite(Number(move?.timestamp))
      ? Number(move.timestamp)
      : Date.now();
    const key = `${String(seq).padStart(6, "0")}_${timestamp}_${index}`;
    out[key] = {
      ...move,
      seq,
    };
  });

  return out;
};

export const sortRecentMovesFromNode = (recentMovesNode = {}) => {
  const rawMoves = isObject(recentMovesNode)
    ? Object.values(recentMovesNode)
    : Array.isArray(recentMovesNode)
      ? recentMovesNode
      : [];

  return rawMoves
    .filter(Boolean)
    .sort((a, b) => {
      const seqA = Number(a?.seq ?? 0);
      const seqB = Number(b?.seq ?? 0);
      if (seqA !== seqB) return seqA - seqB;

      const tsA = Number(a?.timestamp ?? 0);
      const tsB = Number(b?.timestamp ?? 0);
      return tsA - tsB;
    });
};

export const buildLegacyMatchDataFromV2 = ({
  summary,
  playersById,
  recentMovesNode,
  legacyFallback = null,
}) => {
  if (!summary || Number(summary.schemaVersion) !== 2) {
    return legacyFallback || null;
  }

  const playerMap = isObject(playersById) ? playersById : {};
  const orderedIds = Array.isArray(summary.playerOrder)
    ? summary.playerOrder
    : Object.values(playerMap)
        .sort((a, b) => Number(a?.sortIndex ?? 0) - Number(b?.sortIndex ?? 0))
        .map((player) => player?.id)
        .filter(Boolean);

  const players = orderedIds
    .map((playerId) => playerMap[playerId])
    .filter(Boolean)
    .map((player) => {
      const clone = { ...player };
      delete clone.sortIndex;
      return clone;
    });

  return {
    ...(legacyFallback || {}),
    ...summary,
    players,
    moves: sortRecentMovesFromNode(recentMovesNode),
  };
};

export const buildCardCountPayload = (hand, deck) => ({
  handCount: Array.isArray(hand) ? hand.length : 0,
  deckCount: Array.isArray(deck) ? deck.length : 0,
  updatedAt: Date.now(),
});
